import React from "react";
import { useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";

import { AppDispatch } from "../App";
import ClockAnimationIcon from "../icons/ClockAnimationIcon";
import EyeIcon from "../icons/eye_icon.svg?react";
import GaugeIcon from "../icons/gauge_icon.svg?react";
import RainIcon from "../icons/rain_icon.svg?react";
import WindIcon from "../icons/wind_icon.svg?react";
import iconButtonStyles from "../MainScreen/IconButton.module.css";
import { checkPermission } from "../state/appState";
import { ActiveModal, setActiveModal } from "../state/mainGui";
import { getRateLimiterOn, getSimulationStatus, setRateLimiterOnOff } from "../state/simulation";
import { timeConverter } from "../utils/timeConverter";

import { useDurationFormatMessage } from "./Duration";
import { formattedMessageForErrorCodeAsString } from "./PermissionButton";
import styles from "./StatusContainer.module.css";

type Status = "SIMULATION" | "RAIN" | "WIND" | "POWER" | "FOLLOWER";

interface Props {
  type: Status;
  simulationStatus?: {
    status: string;
    time: number; // in seconds
  };
  hidden?: boolean;
  rainDuration?: number; // in seconds
  power?: number; // by %
  followers?: number;
  setSimulationToPaused: () => void;
}

const StatusContainer: React.FC<ReturnType<typeof mapDispatchToProps> & Props> = (props) => {
  const intl = useIntl();

  // Remaining time of rain events
  const remainingTimeText = intl.formatMessage({
    id: "rain_visualization.remaining_time",
    defaultMessage: "Time remaining: ",
  });
  const rainDurationFormatMessage = useDurationFormatMessage(props.rainDuration);

  const rateLimiterOn = useSelector(getRateLimiterOn);
  const rateLimiterAllowedObj = useSelector(checkPermission)("rateLimiterSwitchOnOff");
  const rateLimiterAllowed = rateLimiterAllowedObj.allowed;
  const rateLimiterError = !rateLimiterAllowedObj.allowed
    ? formattedMessageForErrorCodeAsString(rateLimiterAllowedObj.errorCode, intl)
    : "";

  const simulationStatus = useSelector(getSimulationStatus);

  if (props.type === "RAIN") {
    return (
      <div
        className={`
          ${styles.StatusContainer}
          ${styles.Rain}
          ${props.hidden ? styles.Hidden : ""}
        `}
        title={remainingTimeText + rainDurationFormatMessage}
      >
        <div
          className={`
            ${iconButtonStyles.Button} ${iconButtonStyles.Headerbutton}
            ${iconButtonStyles.NotClickable} ${styles.Icon}
          `}
        >
          <RainIcon />
        </div>
      </div>
    );
  } else if (props.type === "WIND") {
    return (
      <div
        className={`
            ${styles.StatusContainer}
            ${styles.Wind}
            ${props.hidden ? styles.Hidden : ""}
          `}
        title={remainingTimeText + rainDurationFormatMessage}
      >
        <div
          className={`
              ${iconButtonStyles.Button} ${iconButtonStyles.Headerbutton}
              ${iconButtonStyles.NotClickable} ${styles.Icon}
            `}
        >
          <WindIcon />
        </div>
      </div>
    );
  } else if (props.type === "SIMULATION") {
    return (
      <div
        className={`
          ${styles.StatusContainer}
          ${props.hidden ? styles.Hidden : ""}
        `}
        title={"d hh:mm"}
      >
        <div
          className={`
            ${iconButtonStyles.Button} ${iconButtonStyles.Headerbutton}
            ${iconButtonStyles.NotClickable} ${styles.Icon}
          `}
        >
          <ClockAnimationIcon
            simulationStatus={(props.simulationStatus && props.simulationStatus.status) + ""}
            animateSpeed={
              (props.simulationStatus && props.simulationStatus.status === "running") ||
              (props.simulationStatus && props.simulationStatus.status === "finished")
                ? 1
                : 0
            }
          />
        </div>
        <div className={styles.Status} style={{ display: "block" }}>
          {props.simulationStatus
            ? timeConverter(props.simulationStatus.time) === "0 00:00"
              ? "d hh:mm"
              : timeConverter(props.simulationStatus.time)
            : null}
        </div>
      </div>
    );
  } else if (props.type === "POWER") {
    return (
      <div
        className={`
          ${styles.StatusContainer}
          ${
            props.power === undefined || !rateLimiterOn
              ? styles.PowerSwitchedOff
              : props.power <= 30
              ? styles.PowerLow
              : props.power <= 70
              ? styles.PowerIntermedeate
              : styles.PowerHigh
          }
          ${props.hidden ? styles.Hidden : ""}
        `}
      >
        <div
          className={`
            ${iconButtonStyles.Button}
            ${iconButtonStyles.Headerbutton}
            ${styles.RateLimiterIcon}
            ${rateLimiterAllowed ? styles.Clickable : styles.NonClickable}
          `}
          title={
            rateLimiterAllowed && rateLimiterOn
              ? intl.formatMessage({
                  id: "rate_limiter.rate_limiter_tooltip_description",
                  defaultMessage:
                    "Rate limiter is on. The percentage indicates how busy the calculation core is.",
                })
              : rateLimiterAllowed && !rateLimiterOn
              ? intl.formatMessage({
                  id: "rate_limiter.rate_limiter_how_to_turn_on",
                  defaultMessage:
                    "Rate limiter is off. You can turn on the rate limiter under 'Preferences' in the right top corner of the screen.",
                })
              : !rateLimiterAllowed && rateLimiterOn
              ? intl.formatMessage(
                  {
                    id: "rate_limiter.rate_limiter_on",
                    defaultMessage:
                      "Rate limiter is on. The percentage indicates how busy the calculation core is.\nTurning off is not allowed: {rateLimiterError}.",
                  },
                  { rateLimiterError }
                )
              : !rateLimiterAllowed && !rateLimiterOn
              ? intl.formatMessage(
                  {
                    id: "rate_limiter.rate_limiter_off",
                    defaultMessage:
                      "Rate limiter is off.\nTurning on is not allowed: {rateLimiterError}.",
                  },
                  { rateLimiterError }
                )
              : ""
          }
          onClick={() => {
            if (rateLimiterAllowed) {
              // Changing preferences will require the simulation to pause (for rate limiter)
              if (simulationStatus.status === "running") props.setSimulationToPaused();
              props.setActiveModal("PREFERENCES");
            }
          }}
        >
          <GaugeIcon />
          <div>
            <span>
              {props.power !== undefined && rateLimiterOn ? `${Math.round(props.power)}%` : "--"}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`
        ${styles.StatusContainer}
        ${styles.Follower}
        ${props.hidden ? styles.Hidden : ""}
      `}
    >
      <div
        className={`
          ${iconButtonStyles.Button} ${iconButtonStyles.Headerbutton}
          ${iconButtonStyles.NotClickable} ${styles.Icon}
        `}
      >
        {props.type === "FOLLOWER" ? <EyeIcon /> : null}
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setRateLimiterOnOff: (turnOn: boolean) => dispatch(setRateLimiterOnOff(turnOn)),
    setActiveModal: (activeModal: ActiveModal) => dispatch(setActiveModal(activeModal)),
  };
};

export default connect(null, mapDispatchToProps)(StatusContainer);
